import fetchWithAuth from "@/api/secureFetch";
import { isCapability } from "./permissions";
import type { AccessEnforcementMode, AccessLevel, AccessSnapshot, AccessSurface } from "./types";

export class AccessRequestError extends Error {
    readonly status: number;
    readonly requestId: string | null;

    constructor(message: string, status: number, requestId: string | null = null) {
        super(message);
        this.name = "AccessRequestError";
        this.status = status;
        this.requestId = requestId;
    }
}

function isAccessLevel(value: unknown): value is AccessLevel {
    return value === null || value === "viewer" || value === "editor" || value === "admin";
}

function isSurface(value: unknown): value is AccessSurface {
    return [
        "intelligenic.fde", "intelligenic.codenexus-product-mesh", "codenexus.fastapi",
        "codenexus.native", "editor.mutations", "collaboration.write"
    ].includes(value as AccessSurface);
}

function isMode(value: unknown): value is AccessEnforcementMode {
    return value === "off" || value === "observe" || value === "enforce";
}

function isEpoch(value: unknown): value is string {
    return typeof value === "string" && /^(?:0|[1-9]\d*):(?:0|[1-9]\d*)$/.test(value);
}

export function parseAccessSnapshot(
    payload: unknown,
    expected: { productId: number; surface: AccessSurface; workspaceBinding: string },
    now = Date.now()
): AccessSnapshot {
    if (!payload || typeof payload !== "object") throw new AccessRequestError("Access response is invalid.", 502);
    const item = payload as Record<string, unknown>;
    const issuedAt = typeof item.issuedAt === "string" ? Date.parse(item.issuedAt) : Number.NaN;
    const expiresAt = typeof item.expiresAt === "string" ? Date.parse(item.expiresAt) : Number.NaN;
    if (
        typeof item.contractVersion !== "string" ||
        typeof item.operationRegistryVersion !== "string" ||
        typeof item.permissionMatrixVersion !== "number" ||
        item.productId !== expected.productId ||
        !Array.isArray(item.permissions) || !item.permissions.every(isCapability) ||
        !Array.isArray(item.allowedOperations) || !item.allowedOperations.every(value => typeof value === "string" && value.length > 0) ||
        !isAccessLevel(item.accessLevel) ||
        !Number.isSafeInteger(item.systemPolicyEpoch) || (item.systemPolicyEpoch as number) < 0 ||
        !Number.isSafeInteger(item.tenantPolicyEpoch) || (item.tenantPolicyEpoch as number) < 0 ||
        !isEpoch(item.policyEpoch) ||
        !isSurface(item.surface) || item.surface !== expected.surface ||
        !isMode(item.enforcementMode) ||
        item.workspaceBinding !== expected.workspaceBinding ||
        !Number.isFinite(issuedAt) || !Number.isFinite(expiresAt) || expiresAt <= issuedAt || expiresAt <= now ||
        typeof item.requestId !== "string" || !item.requestId
    ) {
        throw new AccessRequestError("Access response is invalid.", 502);
    }
    return item as unknown as AccessSnapshot;
}

async function errorFromResponse(response: Response): Promise<AccessRequestError> {
    let requestId: string | null = null;
    try {
        const payload = await response.json() as { error?: { requestId?: unknown } };
        requestId = typeof payload.error?.requestId === "string" ? payload.error.requestId : null;
    } catch {
        // Canonical status still determines behavior when a proxy strips JSON.
    }
    const denied = response.status === 403 || response.status === 404;
    return new AccessRequestError(denied ? "Access denied." : "Unable to load access policy.", response.status, requestId);
}

export async function createAccessSession(
    productId: number,
    token: string,
    surface: AccessSurface,
    workspaceBinding: string,
    signal?: AbortSignal
): Promise<AccessSnapshot> {
    const response = await fetchWithAuth("/api/access/session", token, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ productId, surface, workspaceBinding }),
        signal
    });
    if (!response.ok) throw await errorFromResponse(response);
    return parseAccessSnapshot(await response.json(), { productId, surface, workspaceBinding });
}
